import { memo, useId } from "react";
import { cn } from "@/lib/utils";
import { SquareCheck } from "lucide-react";

type Props = {
  label: string;
  className?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
};

function CheckBox({ checked, className, label, disabled, onChange }: Props) {
  const uid = useId();
  const inputId = `${uid}-checkbox`;

  return (
    <div className="w-full h-full flex flex-col justify-center">
      <label
        htmlFor={inputId}
        className={cn(
          "w-full h-10 rounded px-2 mt-1",
          "flex items-center gap-2 justify-start",
          "transition-colors focus-within:outline-none",
          "text-muted-foreground font-medium text-base",
          "ring-1 ring-input",
          checked ? "ring-primary text-primary bg-primary/5" : "",
          disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
          className
        )}
      >
        <input
          id={inputId}
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          aria-checked={checked}
          aria-disabled={disabled}
          onChange={() => onChange(!checked)}
        />

        <span
          aria-hidden
          className={cn(
            "inline-flex size-5 items-center justify-center transition-all rounded hover:ring-[7px] hover:ring-gray-500/10",
            "peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-chk-accent/40",
            "peer-disabled:opacity-50",
            checked ? "bg-primary text-background" : "bg-transparent border"
          )}
        >
          {checked ? <SquareCheck size={20} strokeWidth={2} /> : null}
        </span>

        {label && (
          <span className="text-[15px] leading-none select-none transition-colors peer-disabled:opacity-70">
            {label}
          </span>
        )}
      </label>
    </div>
  );
}

export default memo(CheckBox);
